<template>
  <div
    class="relative flex items-start text-xxs font-semibold text-headline uppercase"
  >
    <icon-base
      :icon="IconBullet"
      :icon-color="
        list.results.length ? UTIL_COLORS.ERROR : UTIL_COLORS.SUCCESS
      "
      class="mt-0.5"
    />
    <div
      ref="resultRef"
      :class="{
        'cursor-pointer rounded-xs transition-all duration-300 hover:bg-red-100':
          list.results.length,
      }"
      data-cy="watchlist-result-title"
      class="-ml-1 px-1"
      @click="showResultsIfAvailable"
    >
      {{ `${list?.title || "-"} (${list?.abbr || "-"})` }}
      <div
        v-if="resultsAreOpen && list.results.length"
        ref="watchlistDropdown"
        v-on-click-outside="closeResults"
        data-cy="watchlist-results"
        class="absolute z-10 min-w-[300px] rounded-sm bg-white p-4 shadow-sm"
      >
        <lf-h3>{{ $t("DEALS.MIDDESK.WATCHLIST_RESULTS") }}</lf-h3>
        <div class="space-y-4">
          <div
            v-for="result in list.results"
            :key="result.id"
            class="space-y-4"
          >
            <div class="text-xxs text-gray-400 uppercase">
              {{ result.id }}
            </div>
            <div v-if="result.entity_aliases.length" class="text-xs">
              <div>{{ $t("COMMON.ALIAS") }}</div>
              <div class="text-headline">
                {{ result.entity_aliases.join("\n") }}
              </div>
            </div>
            <div class="grid grid-cols-1 space-y-2 text-xs font-normal">
              <lf-link
                v-for="link in getResultLinks(result)"
                :key="link.url"
                :href="link.url"
                :underlined="false"
                target="_blank"
                class="result-link"
              >
                {{ link.name }}
                <icon-base :icon="IconPopup" />
              </lf-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { UTIL_COLORS } from "@/helpers/constants";
import { useTeleportV2 } from "@/hooks/elements";

import IconBullet from "@/components/icons/IconBullet.vue";
import IconPopup from "@/components/icons/IconPopup.vue";

import type {
  WatchlistList,
  WatchlistResults,
} from "@/models/commercialData/Middesk";
import type { PropType } from "vue";

type ResultLink = {
  name: string;
  url: string | undefined;
};

const props = defineProps({
  list: {
    type: Object as PropType<WatchlistList>,
    required: true,
  },
});

const resultsAreOpen = ref(false);
const resultRef = ref<HTMLDivElement | null>(null);
const watchlistDropdown = ref<HTMLElement | null>(null);
const { t } = useI18n();

useTeleportV2({
  target: watchlistDropdown,
  to: "#menus",
  offset: { top: 0 },
  checkForOverflow: true,
});

const getResultLinks = (result: WatchlistResults): ResultLink[] => {
  return [
    {
      name: t("DEALS.MIDDESK.AGENCY_INFORMATION_URL"),
      url: result.agency_information_url,
    },
    {
      name: t("DEALS.MIDDESK.AGENCY_LIST_URL"),
      url: result.agency_list_url,
    },
    {
      name: t("COMMON.URL"),
      url: result.url,
    },
  ].filter((link: ResultLink) => link.url);
};

const showResultsIfAvailable = () => {
  if (props.list.results.length) {
    resultsAreOpen.value = true;
  }
};

const closeResults = () => {
  resultsAreOpen.value = false;
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.result-link {
  @apply flex items-center justify-between;
}
</style>
